import React, { useState } from 'react';
import { Box, Button, Checkbox, Cluster, Grid, Stack, Text } from '@iqueue/iq-ui';
import Modal from '../ui/Modal';
import Field from '../ui/Field';
import Select from '../ui/Select';

const CAPABILITIES = ['orchestrator', 'executor', 'reviewer', 'researcher', 'coder', 'ops'];
const CONTEXT_TYPES = ['none', 'local_directory', 'git_repository', 'local_server'];
/** Пустая модель = «как настроено у раннера»; у пункта меню значение непустое. */
const DEFAULT_MODEL = '__default__';

/**
 * Имя модели для показа: «claude-haiku-4-5-20251001» → «haiku-4-5». Префикс
 * вендора и дата сборки не различают варианты в этом списке, а именно они не
 * влезают в кнопку. Сохраняется по-прежнему полный идентификатор.
 */
const shortModel = (m) => String(m).replace(/^claude-/, '').replace(/-\d{8}$/, '');

/**
 * Половина (или треть) ряда: ширина делится поровну независимо от того, какая
 * подпись длиннее. `Box fill` = inline-size 100%, то есть каждый элемент ряда
 * просит всю ширину и они ужимаются в равные доли; `Stack` внутри растягивает
 * поле на эту долю — сам TextField этого не делает, он inline-flex и встаёт по
 * своим 15rem. Без этой пары обёрток поле с длинной меткой («Иконка (Material
 * Symbols…)») шире соседнего — та самая несимметричность.
 */
function Cell({ children }) {
  return <Box fill><Stack>{children}</Stack></Box>;
}

/**
 * Full admin form for creating/editing an agent (SQLite is source of truth).
 *
 * Ряды — Cluster, как и были. Не Grid: диалог в варианте `basic` меряется по
 * содержимому (`inline-size: fit-content`, минимум 280px), а сетка с `1fr`-
 * колонками наружу ширину не сообщает — диалог схлопывается к минимуму и всё
 * встаёт узкой колонкой. Ряд из полей, наоборот, даёт диалогу его ширину.
 *
 * Выбор из набора — меню (ui/Select): подпись сверху, значение на триггере.
 */
export default function AgentForm({ agent, profiles, runnerTypes, models, onSave, onClose }) {
  const isNew = !agent;
  const [form, setForm] = useState(() => ({
    handle: agent ? agent.handle : '',
    name: agent ? agent.name : '',
    avatar: agent ? (agent.avatar || '') : 'smart_toy',
    color: agent ? (agent.color || '#00ddff') : '#00ddff',
    description: agent ? (agent.description || '') : '',
    system_prompt: agent ? (agent.system_prompt || '') : '',
    model: agent ? (agent.model || '') : '',
    runner_type: agent ? agent.runner_type : (runnerTypes[0] || 'claude'),
    permission_profile: agent ? agent.permission_profile : 'workspace',
    timeout_ms: agent ? agent.timeout_ms : 600000,
    concurrency_limit: agent ? agent.concurrency_limit : 1,
    capabilities: agent ? (agent.capabilities || []) : ['executor'],
    allowed_context_types: agent ? (agent.allowed_context_types || ['none']) : ['none'],
  }));
  const [error, setError] = useState('');
  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));
  // Checkbox приходит из headless-примитива: у него `onCheckedChange`, а не
  // `onChange` — с `onChange` галочки просто не переключались.
  const toggleIn = (k, val) => setForm(f => ({
    ...f, [k]: f[k].includes(val) ? f[k].filter(x => x !== val) : [...f[k], val],
  }));

  const submit = async () => {
    setError('');
    try {
      const body = {
        ...form,
        model: form.model || null,
        timeout_ms: parseInt(form.timeout_ms, 10),
        concurrency_limit: parseInt(form.concurrency_limit, 10),
      };
      if (!isNew) delete body.handle;
      await onSave(body, agent ? agent.id : null);
      onClose();
    } catch (e) {
      setError(e.message);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isNew ? 'Новый агент' : `Агент @${agent.handle}`}
      actions={(
        <>
          <Button variant="text" onClick={onClose}>Отмена</Button>
          <Button onClick={submit}>{isNew ? 'Создать' : 'Сохранить'}</Button>
        </>
      )}
    >
      <Stack gap={250}>
        {/* Подписи — одно-два слова: диалог упёрт в 560dp библиотекой, и длинная
            метка либо режется, либо рвётся на две строки. */}
        {isNew && <Field label="Handle" value={form.handle} onChange={set('handle')} required />}
        <Field label="Имя" value={form.name} onChange={set('name')} required />
        {/* wrap={false}: доли делятся сжатием, а перенос строки разложил бы их
            по одной на ряд и вернул узкую колонку. */}
        <Cluster gap={200} wrap={false}>
          <Cell><Field label="Иконка" value={form.avatar} onChange={set('avatar')} /></Cell>
          <Cell><Field label="Цвет" value={form.color} onChange={set('color')} /></Cell>
        </Cluster>
        <Field label="Описание" value={form.description} onChange={set('description')} multiline rows={2} />
        <Field label="System prompt" value={form.system_prompt} onChange={set('system_prompt')} multiline rows={8} />
        <Cluster gap={200}>
          <Select
            label="Модель"
            value={form.model || DEFAULT_MODEL}
            onChange={(v) => set('model')(v === DEFAULT_MODEL ? '' : v)}
            options={[
              { value: DEFAULT_MODEL, label: 'по умолчанию' },
              ...(models || []).map(m => ({ value: m, label: shortModel(m) })),
            ]}
          />
          <Select label="Runner" value={form.runner_type} onChange={set('runner_type')} options={runnerTypes} />
          <Select label="Профиль" value={form.permission_profile} onChange={set('permission_profile')} options={profiles} />
        </Cluster>
        <Cluster gap={200} wrap={false}>
          <Cell><Field label="Timeout" value={String(form.timeout_ms)} onChange={set('timeout_ms')} /></Cell>
          <Cell><Field label="Параллельность" value={String(form.concurrency_limit)} onChange={set('concurrency_limit')} /></Cell>
        </Cluster>
        <Stack gap={100}>
          <Text variant="label-small" render={<span />}>Роли / capabilities</Text>
          {/* Сетка, а не Cluster: подписи разной длины (orchestrator ↔ ops), и в
              flex-строке каждая следующая галочка начинается там, где кончилась
              предыдущая — колонки не сходятся. Ширину диалогу сетка не сообщает,
              но её здесь и задают ряды полей выше. */}
          <Grid min="compact" gap={200}>
            {CAPABILITIES.map(c => (
              <Checkbox key={c} checked={form.capabilities.includes(c)} onCheckedChange={() => toggleIn('capabilities', c)}>{c}</Checkbox>
            ))}
          </Grid>
        </Stack>
        <Stack gap={100}>
          <Text variant="label-small" render={<span />}>Разрешённые execution contexts</Text>
          <Grid min="compact" gap={200}>
            {CONTEXT_TYPES.map(c => (
              <Checkbox key={c} checked={form.allowed_context_types.includes(c)} onCheckedChange={() => toggleIn('allowed_context_types', c)}>{c}</Checkbox>
            ))}
          </Grid>
        </Stack>
        {error && <Text variant="body-small">{error}</Text>}
      </Stack>
    </Modal>
  );
}
